import React from 'react';
import { Box, Typography, Avatar, Grid } from '@mui/material';
import { Icon } from '@iconify/react';
import { ExternalLink } from 'lucide-react';
import '../../../../../components/common/Panels/UniversalDetailContent.css';

const getAvatarStyles = (name) => {
    const palettes = [
        { bg: '#eff6ff', text: '#3b82f6', border: '#dbeafe' }, // Blue
        { bg: '#ecfdf5', text: '#10b981', border: '#d1fae5' }, // Green
        { bg: '#fff1f2', text: '#f43f5e', border: '#ffe4e6' }, // Rose
        { bg: '#fffbeb', text: '#f59e0b', border: '#fef3c7' }, // Amber
        { bg: '#f5f3ff', text: '#8b5cf6', border: '#ede9fe' }, // Violet
        { bg: '#ecfeff', text: '#06b6d4', border: '#cffafe' }, // Cyan
        { bg: '#f0fdf4', text: '#16a34a', border: '#dcfce7' }, // Green 2
        { bg: '#faf5ff', text: '#9333ea', border: '#f3e8ff' }, // Purple
        { bg: '#fff7ed', text: '#ea580c', border: '#ffedd5' }, // Orange
        { bg: '#fdf2f8', text: '#db2777', border: '#fce7f3' }  // Pink
    ];
    if (!name || name === '-') return palettes[0];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % palettes.length;
    return palettes[index];
};

const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length > 1) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const CustomerLedgerDetail = ({ detail }) => {
    if (!detail) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
                Select a ledger from the list
            </div>
        );
    }

    const customerName = detail.Customer || 'Unknown Customer';
    const avatarStyles = getAvatarStyles(customerName);
    const balanceRemaining = parseFloat(detail.Balance || 0);
    const isBalanceDue = balanceRemaining < 0;

    return (
        <div className="universal-detail-container">
            <div className="universal-detail-content">
                
                {/* Header Section */}
                <div className="universal-detail-header">
                    <div className="universal-detail-icon">
                        <div style={{ color: '#3b82f6', background: '#eff6ff', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon icon="ri:file-list-3-line" fontSize={22} />
                        </div>
                    </div>
                    <h1 className="universal-detail-title">
                        {customerName}
                    </h1>
                </div>

                {/* Related To Section */}
                <div className="universal-related-section">
                    <div
                        className="universal-related-avatar"
                        style={{ backgroundColor: avatarStyles.bg, color: avatarStyles.text, border: `1px solid ${avatarStyles.border}` }}
                    >
                        {getInitials(customerName)}
                    </div>
                    <div className="universal-related-info">
                        <span className="universal-related-label">Related To:</span>
                        <span className="universal-related-name">
                            {customerName}
                        </span>
                    </div>
                </div>

                {/* Metadata Grid */}
                <div className="universal-metadata-grid">
                    <div className="universal-metadata-item">
                        <span className="universal-metadata-label">Lead No:</span>
                        <span className="universal-metadata-value">{detail.LeadNo || 'N/A'}</span>
                    </div>
                    <div className="universal-metadata-item">
                        <span className="universal-metadata-label">Email:</span>
                        <span className="universal-metadata-value" style={{ wordBreak: 'break-all' }}>{detail.Email || 'N/A'}</span>
                    </div>
                    <div className="universal-metadata-item">
                        <span className="universal-metadata-label">Credit Amount:</span>
                        <span className="universal-metadata-value">
                            {detail.CurrencySymbol} {parseFloat(detail.Credit || 0).toLocaleString()}
                        </span>
                    </div>
                    <div className="universal-metadata-item">
                        <span className="universal-metadata-label">Paid Amount:</span>
                        <span className="universal-metadata-value">
                            {detail.CurrencySymbol} {parseFloat(detail.Revenue || 0).toLocaleString()}
                        </span>
                    </div>
                </div>

                {/* Ledger Summary Box */}
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 3, mt: 4, backgroundColor: '#f8fafc' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#334155', mb: 2 }}>Ledger Summary</Typography>
                    <Typography sx={{ fontSize: '14px', color: '#64748b', mb: 3 }}>
                        Total invoice amount generated for this customer is <strong>{detail.CurrencySymbol} {parseFloat(detail.TotalInvoice || 0).toLocaleString()}</strong>.
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontSize: '15px', color: '#475569', fontWeight: 600 }}>Total Balance Remaining:</Typography>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: isBalanceDue ? '#ef4444' : '#1e293b' }}>
                            {detail.CurrencySymbol} {balanceRemaining.toLocaleString()}
                        </Typography>
                    </Box>
                </Box>
                
            </div>
        </div>
    );
};

export default CustomerLedgerDetail;
